import { TanStackDevtools } from "@tanstack/react-devtools";
import {
	createRootRoute,
	HeadContent,
	Scripts,
	useLoaderData,
} from "@tanstack/react-router";
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools";
import { RelayEnvironmentProvider } from "react-relay";
import { Toaster } from "sonner";
import { UploadProgressProvider } from "@/contexts/UploadProgressContext";
import { environment } from "@/relay/environment";
import { getSession } from "@/utils/getSession";
import appCss from "../styles.css?url";
import { ThemeProvider } from "@/hooks/useTheme";
import { getDefaultAppearance } from "@/handlers/getDefaultAppearance";
import { useEffect } from "react";
import * as Sentry from "@sentry/tanstackstart-react";
import { TopLoader } from "@/components/ui/top-loader";
import { getImageUrl } from "@/utils/getImageUrl";

export const Route = createRootRoute({
	shellComponent: RootDocument,
	beforeLoad: async () => {
		try {
			const session = await getSession();

			return { session };
		} catch (err) {
			console.error("unable to run beforeLoad script: ", err);
			throw err;
		}
	},
	loader: async ({ context }) => {
		const defaultTheme = await getDefaultAppearance();

		return { session: context.session, defaultTheme };
	},
	head: () => {
		const favicon16Url = getImageUrl(
			"https://scarf-public-assets.s3.us-east-1.amazonaws.com/scarf/Scarf-Badge-Primary.svg",
			{ format: "ico", width: 16, height: 16, quality: 100 },
		);
		const favicon32Url = getImageUrl(
			"https://scarf-public-assets.s3.us-east-1.amazonaws.com/scarf/Scarf-Badge-Primary.svg",
			{ format: "ico", width: 32, height: 32, quality: 100 },
		);
		const favicon96Url = getImageUrl(
			"https://scarf-public-assets.s3.us-east-1.amazonaws.com/scarf/Scarf-Badge-Primary.svg",
			{ format: "ico", width: 96, height: 96, quality: 100 },
		);
		const appleTouchIconUrl = getImageUrl(
			"https://scarf-public-assets.s3.us-east-1.amazonaws.com/scarf/Scarf-Badge-Primary.svg",
			{ format: "ico", width: 180, height: 180, quality: 100 },
		);
		const pwaIcon512Url = getImageUrl(
			"https://scarf-public-assets.s3.us-east-1.amazonaws.com/scarf/Scarf-Badge-Primary.svg",
			{ format: "ico", width: 512, height: 512, quality: 100 },
		);

		const ogImageUrl =
			"https://cdn.scarf.club/insecure/q:75/f:jpg/aHR0cHM6Ly9zMy5hcmNodmUuYXNzZXRzLnMzLnVzLWVhc3QtMS5hbWF6b25hd3MuY29tL3NjYXJmLWVhc3RldHMvY2FyZHByaW1hcnkuc3Zn";

		const currentUrl =
			typeof window !== "undefined"
				? window.location.href
				: "https://painel.scarf.club";

		const canonicalUrl = currentUrl;

		return {
			meta: [
				{
					charSet: "utf-8",
				},
				{
					name: "viewport",
					content: "width=device-width, initial-scale=1",
				},
				{
					title: "Scarf | Painel",
				},
				{
					name: "description",
					content:
						"Scarf tem tudo para você construir seu negócio digital. Área de membros customizada para seus cursos, mentorias, comunidade, gamification e muito mais.",
				},
				{
					name: "google",
					content: "notranslate",
				},
				{
					name: "keywords",
					content:
						"scarf, área de membros, comunidade, cursos online, mentorias, gamification, negócio digital, plataforma de vendas, plataforma de cursos, comunidade online, membership",
				},
				{
					name: "description",
					content:
						"Scarf tem tudo para você construir seu negócio digital. Área de membros customizada para seus cursos, mentorias, comunidade, gamification e muito mais.",
				},
				{
					name: "google",
					content: "notranslate",
				},
				{
					name: "keywords",
					content:
						"scarf, área de membros, comunidade, cursos online, mentorias, gamification, negócio digital, plataforma de vendas, plataforma de cursos, comunidade online, membership",
				},
				{
					name: "author",
					content: "Scarf",
				},
				{
					name: "theme-color",
					content: "#036141",
				},
				{
					name: "robots",
					content: "index,follow",
				},
				{
					property: "article:author",
					content: "Scarf",
				},
				{
					property: "article:publisher",
					content: "https://www.scarf.club",
				},
				// Open Graph / Facebook
				{
					property: "og:type",
					content: "website",
				},
				{
					property: "og:url",
					content: currentUrl,
				},
				{
					property: "og:title",
					content: "Scarf | Painel",
				},
				{
					property: "og:description",
					content:
						"Scarf tem tudo para você construir seu negócio digital. Área de membros customizada para seus cursos, mentorias, comunidade, gamification e muito mais.",
				},
				{
					property: "og:image",
					content: ogImageUrl,
				},
				{
					property: "og:image:secure_url",
					content: ogImageUrl,
				},
				{
					property: "og:image:width",
					content: "1200",
				},
				{
					property: "og:image:height",
					content: "630",
				},
				{
					property: "og:image:alt",
					content: `Scarf - Plataforma de Área de Membros`,
				},
				{
					property: "og:image:type",
					content: "image/png",
				},
				{
					property: "og:site_name",
					content: "Scarf",
				},
				{
					property: "og:locale",
					content: "pt_BR",
				},
				// Twitter
				{
					property: "twitter:card",
					content: "summary_large_image",
				},
				{
					property: "twitter:url",
					content: currentUrl,
				},
				{
					property: "twitter:title",
					content: "Scarf | Painel",
				},
				{
					property: "twitter:description",
					content:
						"Scarf tem tudo para você construir seu negócio digital. Área de membros customizada para seus cursos, mentorias, comunidade, gamification e muito mais.",
				},
				{
					property: "twitter:image",
					content: ogImageUrl,
				},
				{
					property: "twitter:image:alt",
					content: `Scarf - Plataforma de Área de Membros`,
				},
				// Apple touch icon
				{
					name: "apple-mobile-web-app-capable",
					content: "yes",
				},
				{
					name: "apple-mobile-web-app-status-bar-style",
					content: "default",
				},
				{
					name: "apple-mobile-web-app-title",
					content: "Scarf",
				},
			],
			links: [
				{
					rel: "stylesheet",
					href: appCss,
				},
				{
					rel: "canonical",
					href: canonicalUrl,
				},
				{
					rel: "sitemap",
					type: "application/xml",
					href: "/sitemap.xml",
				},
				{
					rel: "robots",
					type: "text/plain",
					href: "/robots.txt",
				},
				{
					rel: "icon",
					type: "image/x-icon",
					sizes: "16x16",
					href: favicon16Url,
				},
				{
					rel: "icon",
					type: "image/x-icon",
					sizes: "32x32",
					href: favicon32Url,
				},
				{
					rel: "icon",
					type: "image/png",
					sizes: "96x96",
					href: favicon96Url,
				},
				{
					rel: "apple-touch-icon",
					sizes: "180x180",
					href: appleTouchIconUrl,
				},
				{
					rel: "icon",
					type: "image/png",
					sizes: "512x512",
					href: pwaIcon512Url,
				},
				{
					rel: "manifest",
					href: "/manifest",
				},
				{
					rel: "preconnect",
					href: "https://fonts.googleapis.com",
				},
				{
					rel: "preconnect",
					href: "https://fonts.gstatic.com",
					crossOrigin: "anonymous",
				},
			],
			scripts: [
				{
					type: "application/ld+json",
					children: JSON.stringify({
						"@context": "https://schema.org",
						"@graph": [
							{
								"@type": "Organization",
								"@id": "https://www.scarf.club/#organization",
								name: "Scarf",
								url: "https://www.scarf.club",
								logo: "https://assets.scarf.club/2L3ynAdQWKTcIPmIRW3BCqkF0cdH3cKaZ_OECQhDRm0/w:32/q:80/f:ico/aHR0cHM6Ly9zMy5hcmNodmUuYXNzZXRzLnMzLnVzLWVhc3QtMS5hbWF6b25hd3MuY29tL3NjYXJmLWVhc3RldHMvY2FyZHByaW1hcnkuc3Zn",
								description:
									"Scarf tem tudo para você construir seu negócio digital. Área de membros customizada para seus cursos, mentorias, comunidade, gamification e muito mais.",
								sameAs: ["https://instagram.com/scarflabs"],
								contactPoint: {
									"@type": "ContactPoint",
									contactType: "customer service",
									url: "https://www.scarf.club",
								},
							},
							{
								"@type": "WebSite",
								"@id": "https://painel.scarf.club/#website",
								name: "Scarf",
								url: "https://painel.scarf.club",
								publisher: {
									"@id": "https://www.scarf.club/#organization",
								},
								potentialAction: {
									"@type": "SearchAction",
									target: {
										"@type": "EntryPoint",
										urlTemplate:
											"https://painel.scarf.club/search?q={search_term_string}",
									},
									"query-input": {
										"@type": "PropertyValueSpecification",
										valueRequired: "http://schema.org/True",
										valueName: "search_term_string",
									},
								},
							},
							{
								"@type": "SoftwareApplication",
								"@id": "https://painel.scarf.club/#software",
								name: "Scarf",
								applicationCategory: "BusinessApplication",
								operatingSystem: "Web",
								offers: {
									"@type": "Offer",
									price: "0",
									priceCurrency: "BRL",
									availability: "https://schema.org/InStock",
								},
								aggregateRating: {
									"@type": "AggregateRating",
									ratingValue: "4.8",
									ratingCount: "150",
								},
								creator: {
									"@id": "https://www.scarf.club/#organization",
								},
							},
						],
					}),
				},
			],
		};
	},
	errorComponent: ({ error }) => {
		useEffect(() => {
			Sentry.captureException(error);
		}, [error]);

		return (
			<main className="flex h-full w-full items-center justify-center">
				<div className="flex flex-col items-center justify-center gap-4">
					<div
						className="flex h-fit max-h-[570px] w-fit items-center px-4"
						style={{ aspectRatio: 444 / 570 }}
					>
						<svg
							width="444"
							height="570"
							viewBox="0 0 444 570"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
							className="w-full"
							style={{ aspectRatio: 444 / 570 }}
						>
							<title>Error</title>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M391.086 5.0293C393.353 5.44857 395.485 6.28 397.482 7.51649C401.476 11.5955 404.084 16.4491 405.299 22.0844C405.171 22.8518 404.929 23.5625 404.588 24.2162C402.357 25.2964 399.991 25.7725 397.482 25.6375C396.963 24.7776 396.722 23.8325 396.771 22.795C393.041 23.7614 391.143 22.3402 391.086 18.5312C390.603 18.2754 390.127 18.0338 389.665 17.8206C389.004 17.0602 388.763 16.1151 388.954 14.9781C391.634 13.9974 394.234 12.8107 396.771 11.4249C396.622 11.0057 396.388 10.6504 396.061 10.359C394.178 10.0037 392.287 9.88999 390.376 10.0037C389.9 10.0037 389.665 9.76918 389.665 9.29306C389.573 8.02104 389.807 6.83429 390.376 5.73992C390.852 5.73992 391.086 5.50542 391.086 5.0293Z"
								fill="#FCAC94"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M53.5419 4.31756C54.0465 4.95712 54.7571 5.19874 55.6738 5.02819V7.8707H49.9888C49.9106 9.29906 50.3867 10.4858 51.41 11.4238V13.5557C47.1818 13.4633 46.9402 13.9395 50.6994 14.977C53.5561 14.508 54.5013 15.4531 53.5419 17.8195C50.8913 17.5921 48.2904 17.8266 45.725 18.5301C47.8072 19.2265 49.939 19.4681 52.1207 19.2407V21.3726H42.1719C43.5363 15.8013 45.3128 10.3579 47.5016 5.02819C49.4345 4.3602 51.4456 4.11858 53.5419 4.31756Z"
								fill="#FBB099"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M51.412 11.4238H52.1227C52.4353 12.5395 53.146 13.3709 54.2545 13.911C52.9186 14.0531 51.7318 14.4085 50.7014 14.977C46.9422 13.9394 47.1838 13.4633 51.412 13.5557V11.4238Z"
								fill="#ED6F5A"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M53.5415 17.8202V18.5308C52.838 18.41 52.3619 18.6445 52.1203 19.2414C49.9386 19.4688 47.8067 19.2272 45.7246 18.5308C48.29 17.8273 50.8909 17.5928 53.5415 17.8202Z"
								fill="#E86D58"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M396.063 37.0067C392.418 42.8622 388.751 48.7818 385.048 54.7724C384.785 56.3357 384.43 57.8778 383.982 59.3914C385.361 60.4076 386.306 61.7081 386.825 63.2999C381.929 60.8269 376.954 58.4605 371.902 56.1936C370.644 55.2058 369.223 54.3744 367.638 53.7064C364.092 53.3511 360.539 53.2303 356.979 53.3511C360.39 52.6476 363.943 52.4131 367.638 52.6405C367.148 49.9472 367.858 47.7016 369.77 45.8895C379.321 40.8227 388.68 35.493 397.84 29.9004C397.761 32.3947 397.164 34.7682 396.063 37.0067Z"
								fill="#C18E80"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M54.9596 34.1641C59.6923 38.1862 64.9012 41.5048 70.5934 44.1128V50.5085C70.5862 51.5886 70.1101 52.413 69.1721 52.9957C67.495 53.8342 65.8393 54.6657 64.1977 55.4829C60.8649 49.5278 57.667 43.3738 54.6042 37.0066C54.0571 35.8909 54.1779 34.9458 54.9596 34.1641Z"
								fill="#F9B59F"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M54.9652 34.1645C54.1835 34.9462 54.0627 35.8913 54.6099 37.007C57.6727 43.3742 60.8705 49.5282 64.2034 55.4833C63.0593 56.3858 61.7588 57.0964 60.2949 57.6152C59.9467 59.0648 60.0675 60.4861 60.6502 61.8789C60.2096 61.8008 59.8543 61.5663 59.5843 61.1683C57.8504 53.4651 55.3632 46.1243 52.1227 39.1389C51.9806 36.9928 51.739 34.8609 51.4121 32.7432C51.2557 31.9047 51.4902 31.194 52.1227 30.6113C53.2384 31.6631 54.1835 32.8427 54.9652 34.1645Z"
								fill="#C18E80"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M70.5957 44.1133C71.285 45.6127 71.5195 47.2685 71.3063 49.0877C73.1255 48.8745 74.7813 49.109 76.2807 49.7983C74.3052 49.642 72.4078 49.8765 70.5957 50.5089V44.1133Z"
								fill="#16212B"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M371.9 56.1934C376.953 58.4603 381.927 60.8267 386.823 63.2996C386.788 65.929 386.546 68.537 386.113 71.1165C380.065 85.5565 373.911 100.004 367.636 114.465C366.229 118.011 364.808 121.564 363.373 125.124C361.887 126.965 360.942 129.097 360.53 131.52C358.036 135.556 355.904 139.82 354.135 144.311C353.133 144.645 352.664 145.356 352.713 146.443C350.404 150.351 348.272 154.381 346.318 158.524C328.616 178.237 310.026 197.189 290.533 215.374C287.598 218.415 285.111 221.734 283.072 225.323C282.539 227.277 281.828 229.167 280.94 231.008C280.194 228.712 279.725 226.346 279.519 223.901C279.391 221.606 278.922 219.474 278.097 217.506V216.795C277.678 212.56 276.733 208.538 275.255 204.714V202.583C273.578 193.217 271.68 183.978 269.57 174.868C268.881 173.369 268.646 171.713 268.859 169.894C269.392 168.593 270.103 167.407 270.991 166.341C273.272 164.372 275.88 162.951 278.808 162.077C278.581 163.178 277.87 163.647 276.676 163.498C276.463 170.896 276.939 178.237 278.097 185.528C279.526 193.145 281.189 200.728 283.072 208.268C284.358 208.268 285.538 207.919 286.625 207.202C306.679 187.858 324.089 166.426 338.856 142.89C351.093 117.428 361.163 91.1349 369.058 64.0103C370.33 61.516 371.282 58.908 371.9 56.1934Z"
								fill="#131B25"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M80.5447 57.6152C81.5538 59.0436 82.03 60.7065 81.966 62.5896C80.9569 61.1613 80.4808 59.4984 80.5447 57.6152Z"
								fill="#161824"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M367.639 53.7074C364.093 53.3521 360.54 53.2312 356.979 53.3521C356.503 53.5866 356.034 53.8282 355.558 54.0627V55.4839C353.177 60.9984 350.804 66.4418 348.452 71.8284C344.081 84.4562 339.107 96.7714 333.529 108.781C333.344 110.536 332.633 111.957 331.397 113.045V113.755C331.446 114.843 330.977 115.553 329.975 115.887C328.163 120.521 326.031 125.019 323.58 129.389C309.424 141.427 294.501 152.321 278.81 162.078C278.583 163.18 277.872 163.649 276.678 163.499C276.465 170.897 276.941 178.238 278.1 185.529C279.528 193.147 281.191 200.729 283.074 208.269C284.36 208.269 285.54 207.921 286.627 207.203C306.681 187.86 324.091 166.427 338.858 142.891C351.095 117.429 361.165 91.1361 369.06 64.0115C370.332 61.5172 371.284 58.9092 371.902 56.1946C370.645 55.2068 369.223 54.3754 367.639 53.7074Z"
								fill="#15212B"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M60.6484 63.3008C61.9844 64.9068 62.695 66.7971 62.7803 68.9858C61.8139 68.2112 61.1032 67.2661 60.6484 66.1433V63.3008Z"
								fill="#161824"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M81.9687 63.3008C83.127 66.4986 84.0722 69.8101 84.8112 73.2496C82.4448 70.4426 81.4926 67.124 81.9687 63.3008Z"
								fill="#161824"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M386.826 63.3008C387.295 64.2175 387.529 65.2834 387.536 66.4986C387.65 68.3036 387.181 69.8457 386.115 71.1177C386.549 68.5381 386.79 65.9301 386.826 63.3008Z"
								fill="#861E21"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M86.9414 81.7773H87.652C88.2703 84.1153 88.7464 86.4817 89.0733 88.8836C87.8084 86.7446 87.0977 84.3782 86.9414 81.7773Z"
								fill="#161824"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M221.96 89.5948C216.708 89.41 211.5 89.6445 206.326 90.3054C206.404 89.8648 206.639 89.5095 207.037 89.2394C212.139 88.6567 217.113 88.7775 221.96 89.5948Z"
								fill="#431E1B"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M89.783 90.3047C90.7921 91.7402 91.2682 93.3959 91.2043 95.2791C90.1952 93.8436 89.7191 92.1879 89.783 90.3047Z"
								fill="#161824"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M202.063 91.7249C201.345 92.5564 200.393 93.0325 199.221 93.1462C199.782 92.0092 200.727 91.5331 202.063 91.7249Z"
								fill="#5D211C"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M91.2049 95.2793C92.214 96.7148 92.6901 98.3705 92.6262 100.254C91.6171 98.8182 91.1409 97.1625 91.2049 95.2793Z"
								fill="#161824"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M76.281 49.7988C77.4678 50.7439 78.6474 51.6962 79.8342 52.6413C80.1824 54.2829 80.424 55.9386 80.5448 57.6157C80.4808 59.4989 80.957 61.1617 81.966 62.5901V63.3007C81.4899 67.1239 82.4422 70.4425 84.8086 73.2495C85.5192 76.092 86.2298 78.9345 86.9404 81.777C87.0968 84.3779 87.8074 86.7443 89.0723 88.8833C89.6692 89.1249 89.9038 89.601 89.7829 90.3046C89.719 92.1877 90.1951 93.8435 91.2042 95.2789C91.1402 97.1621 91.6164 98.8179 92.6255 100.253C94.2528 104.389 95.674 108.653 96.8892 113.045C96.7187 114.75 97.1948 116.171 98.3105 117.308C99.817 120.798 101.352 124.351 102.93 127.968C105.8 130.491 108.764 132.857 111.812 135.074C112.814 136.787 114.236 137.973 116.076 138.627C122.657 144.994 129.763 150.679 137.395 155.682C139.157 157.629 141.289 159.051 143.791 159.946C144.736 160.657 145.688 161.367 146.633 162.078C146.249 162.121 146.008 162.355 145.923 162.789C146.171 163.641 146.527 163.641 146.988 162.789C146.733 177.626 145.191 192.315 142.369 206.847C141.623 206.954 140.913 206.833 140.237 206.492C121.981 188.698 105.751 169.156 91.5595 147.865C88.3404 141.761 85.2634 135.6 82.3214 129.389C80.3956 124.216 78.3774 119.12 76.281 114.111C76.8353 112.853 76.7145 111.552 75.9257 110.202C75.1796 108.291 74.1136 106.635 72.7279 105.228C70.7381 95.7337 68.1372 86.4956 64.911 77.5133C64.4917 74.5997 63.7811 71.7572 62.7791 68.9857C62.6938 66.797 61.9832 64.9067 60.6472 63.3007V61.8795C60.0645 60.4866 59.9437 59.0654 60.2919 57.6157C61.7558 57.0969 63.0563 56.3863 64.2004 55.4838C65.8419 54.6666 67.4977 53.8352 69.1748 52.9966C70.1128 52.4139 70.5889 51.5896 70.596 50.5094C72.4081 49.877 74.3055 49.6425 76.281 49.7988Z"
								fill="#16212B"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M221.96 89.5942H222.671C225.18 90.6388 227.78 91.5911 230.488 92.4367C233.117 95.7624 236.081 98.8466 239.371 101.675C240.28 103.878 241.581 105.775 243.279 107.36C245.447 115.02 245.923 122.837 244.701 130.811C244.274 131.805 243.564 132.516 242.569 132.942C241.922 130.59 240.977 130.349 239.726 132.232C239.25 130.1 238.781 127.968 238.305 125.836C234.29 115.17 226.473 109.954 214.854 110.202C206.86 110.11 200.343 113.194 195.312 119.441C193.741 121.594 192.675 123.967 192.114 126.547C191.538 127.819 191.183 129.24 191.048 130.811L188.916 122.283C188.81 123.96 188.81 125.616 188.916 127.257C187.971 126.49 186.905 126.014 185.718 125.836C185.285 124.124 185.043 122.347 185.008 120.506C185.797 116.15 186.386 111.766 186.784 107.36C189.307 103.238 192.15 99.4435 195.312 95.9898C196.342 94.597 197.643 93.6448 199.22 93.1473C200.393 93.0336 201.345 92.5575 202.063 91.7261C203.228 90.6033 204.65 90.1272 206.327 90.3048C211.5 89.6439 216.709 89.4094 221.96 89.5942Z"
								fill="#161D27"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M238.304 125.836C238.574 129.986 238.098 134.015 236.883 137.916C236.222 138.677 235.98 139.622 236.172 140.759H238.304C236.549 141.569 235.838 142.99 236.172 145.023H194.956C193.286 139.004 192.334 132.842 192.113 126.546C192.675 123.967 193.741 121.593 195.311 119.44C200.342 113.194 206.859 110.109 214.853 110.202C226.472 109.953 234.289 115.169 238.304 125.836Z"
								fill="#F6A58D"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M192.114 126.547C192.335 132.843 193.287 139.004 194.957 145.023H193.536C192.903 144.59 192.193 144.355 191.404 144.313C190.281 143.147 189.805 141.726 189.983 140.049C190.842 140.568 191.788 140.809 192.825 140.76C191.873 138.016 191.283 135.174 191.048 132.232C189.926 131.308 189.101 131.543 188.561 132.943H187.14C187.332 131.806 187.09 130.86 186.429 130.1C185.967 128.729 185.733 127.307 185.719 125.836C186.905 126.014 187.971 126.49 188.917 127.258C188.81 125.616 188.81 123.96 188.917 122.283L191.048 130.811C191.183 129.24 191.539 127.819 192.114 126.547Z"
								fill="#251D25"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M238.302 125.836C238.778 127.968 239.247 130.1 239.723 132.232C239.453 134.733 238.743 137.106 237.592 139.338C237.023 139.011 236.789 138.542 236.881 137.917C238.096 134.015 238.572 129.986 238.302 125.836Z"
								fill="#371F24"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M242.565 132.942C242.629 135.209 242.16 137.341 241.144 139.338C240.199 139.814 239.246 140.283 238.301 140.759H236.169C235.977 139.622 236.219 138.677 236.88 137.917C236.788 138.542 237.022 139.011 237.591 139.338C238.742 137.106 239.452 134.733 239.722 132.232C240.973 130.348 241.918 130.59 242.565 132.942Z"
								fill="#ECBCAB"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M189.982 140.049C189.065 137.775 188.596 135.408 188.561 132.942C189.101 131.543 189.925 131.308 191.048 132.232C191.282 135.174 191.872 138.016 192.824 140.759C191.787 140.809 190.842 140.567 189.982 140.049Z"
								fill="#E0B5A9"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M221.959 189.082C222.954 191.988 223.423 195.066 223.381 198.32C222.833 197.78 222.478 197.069 222.315 196.188C219.927 197.069 217.44 197.546 214.853 197.61C214.242 234.555 214.007 271.508 214.142 308.467C213.659 315.623 213.19 322.843 212.721 330.142C213.425 338.733 213.659 347.374 213.432 356.079C212.934 353.663 212.465 351.176 212.01 348.618C212.06 343.182 212.302 337.973 212.721 332.984C212.131 306.385 211.776 279.737 211.655 253.039C211.421 251.141 211.179 249.251 210.945 247.353C210.71 253.749 210.468 260.145 210.234 266.54C210.092 265.204 209.736 264.018 209.168 262.987V241.668C210.326 229.012 210.802 216.221 210.589 203.295V196.188C211.286 193.971 211.52 191.598 211.3 189.082H221.959Z"
								fill="#10141D"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M160.844 168.474C156.104 166.342 151.372 164.21 146.632 162.078C146.248 162.121 146.006 162.355 145.921 162.789C146.17 163.642 146.525 163.642 146.987 162.789C146.731 177.627 145.189 192.316 142.368 206.848C141.622 206.954 140.911 206.834 140.236 206.493C121.98 188.698 105.749 169.156 91.5581 147.866C88.339 141.761 85.262 135.6 82.32 129.389C80.3942 124.216 78.3831 119.121 76.2796 114.111C76.8339 112.853 76.7131 111.553 75.9243 110.203C75.1782 108.291 74.1122 106.635 72.7265 105.228C70.7368 95.7342 68.1359 86.496 64.9096 77.5137C64.7959 79.4253 64.9096 81.3155 65.2649 83.1987C66.9846 89.2177 68.882 95.1372 70.9499 100.964C71.5255 104.745 72.0017 108.533 72.3712 112.334C72.6341 113.749 73.2311 114.935 74.1478 115.888C74.7447 116.129 74.9792 116.605 74.8584 117.309C75.4837 122.639 76.4289 127.855 77.7009 132.943C78.2126 133.241 78.5679 133.71 78.7668 134.364C79.1222 136.482 79.243 138.613 79.1222 140.76C79.1008 143.865 79.3353 146.942 79.8328 149.998H80.5434C80.8632 153.224 81.8154 156.308 83.3859 159.236C85.0772 160.927 86.4985 162.817 87.6497 164.921C88.0618 166.52 89.007 167.699 90.4922 168.474C90.9044 170.073 91.8495 171.253 93.3347 172.027C94.0453 172.262 94.5215 172.738 94.756 173.448C108.642 189.111 123.209 204.275 138.46 218.929C139.767 221.082 141.075 223.214 142.368 225.324H143.079C143.427 226.966 143.668 228.621 143.789 230.299C143.697 232.48 143.931 234.612 144.5 236.694C145.545 239.835 146.021 243.154 145.921 246.643V248.064C146.959 251.688 147.428 255.476 147.342 259.434V260.856C147.819 268.672 148.288 276.489 148.764 284.306C149.105 279.929 149.581 275.665 150.185 271.515V270.094C149.972 267.045 150.441 264.203 151.606 261.566V259.434C152.146 252.882 153.091 246.487 154.449 240.247C154.847 231.052 155.792 222.048 157.291 213.244V208.98C157.639 206.734 158.116 204.602 158.712 202.584V201.873C159.11 197.837 159.587 193.808 160.134 189.793C161.079 182.921 162.031 176.056 162.976 169.185C162.059 169.355 161.349 169.114 160.844 168.474Z"
								fill="#161824"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M223.379 198.319C223.258 199.023 223.492 199.499 224.089 199.741V206.847C224.267 211.757 224.978 216.49 226.221 221.059C227.252 229.331 228.204 237.624 229.064 245.931C229.284 251.204 229.76 256.413 230.485 261.565C231.309 268.181 232.02 274.811 232.617 281.463C233.818 290.673 234.77 299.911 235.459 309.177H237.591C238.167 307.664 238.522 306.008 238.657 304.203C238.856 304.857 239.211 305.326 239.723 305.624V307.756C241.258 309.064 243.155 309.895 245.408 310.243C249.16 310.442 252.834 310.563 256.423 310.599C258.341 324.285 258.931 338.022 258.199 351.815C254.064 353.67 249.8 355.091 245.408 356.079C244.989 355.929 244.633 355.695 244.342 355.368C234.358 356.69 224.288 357.4 214.14 357.5V308.467C214.005 271.507 214.24 234.554 214.851 197.609C217.438 197.545 219.925 197.069 222.313 196.188C222.476 197.069 222.831 197.779 223.379 198.319Z"
								fill="#16212A"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M256.782 302.07H257.492V306.334H260.335C260.811 306.1 261.28 305.858 261.756 305.623C264.172 306.327 266.538 306.092 268.862 304.913C271.044 305.005 273.176 304.771 275.258 304.202H278.1C278.015 309.198 278.25 314.172 278.811 319.125C279.287 326.942 279.756 334.759 280.232 342.576C280.147 347.572 280.382 352.546 280.943 357.499C279.856 358.814 278.434 359.525 276.679 359.631H273.126C272.899 363.085 273.133 366.403 273.837 369.58C274.128 396.392 273.659 423.161 272.415 449.881C271.975 449.803 271.619 449.568 271.349 449.17C271.122 451.402 270.298 453.292 268.862 454.855C268.237 452.851 267.405 450.954 266.375 449.17C265.43 437.324 264.598 425.478 263.888 413.639C264.328 413.717 264.684 413.951 264.954 414.349C265.508 410.761 265.387 407.208 264.598 403.69C265.053 400.236 265.529 396.797 266.02 393.386C265.21 392.299 264.499 391.354 263.888 390.543C264.954 389.591 265.664 388.412 266.02 386.99C265.707 385.903 265.465 384.837 265.309 383.792C266.126 382.918 266.958 382.918 267.796 383.792C269.04 382.378 269.751 380.723 269.928 378.818C270.497 372.621 270.383 366.467 269.573 360.342C265.508 359.482 261.479 360.079 257.492 362.118C242.825 362.104 228.136 362.225 213.433 362.474V356.078C213.661 347.373 213.426 338.732 212.723 330.14C213.192 322.842 213.661 315.622 214.144 308.466V357.499C224.292 357.4 234.361 356.689 244.346 355.367C244.637 355.694 244.992 355.929 245.412 356.078C249.803 355.09 254.067 353.669 258.203 351.814C258.935 338.021 258.345 324.285 256.426 310.598C252.838 310.562 249.164 310.442 245.412 310.243C243.159 309.894 241.261 309.063 239.727 307.755C240.821 308.324 242.008 308.558 243.28 308.466V304.913C243.756 305.858 244.225 306.81 244.701 307.755H246.833C247.408 306.483 247.764 305.062 247.899 303.492C248.041 304.828 248.396 306.014 248.965 307.045C249.675 307.99 250.386 307.99 251.097 307.045C251.885 307.002 252.596 306.768 253.228 306.334C254.238 307.009 255.424 307.244 256.782 307.045V302.07Z"
								fill="#131B25"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M149.476 310.6C149.59 312.277 149.476 313.932 149.121 315.574C148.851 315.972 148.495 316.206 148.055 316.285C148.104 314.202 148.573 312.312 149.476 310.6Z"
								fill="#B55432"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M152.319 312.73V318.415H154.451C154.849 319.332 155.56 319.922 156.583 320.192C173.723 323.098 190.778 324.285 207.748 323.745L207.037 323.034C208.402 322.778 209.589 322.188 210.591 321.258C211.408 333.573 211.408 345.895 210.591 358.21C189.03 357.528 167.47 356.583 145.924 355.368C145.476 354.714 145.241 354.003 145.213 353.236C145.867 346.634 146.101 339.997 145.924 333.338C146.158 327.653 146.4 321.968 146.634 316.283H148.055C148.496 316.205 148.851 315.97 149.121 315.573C149.477 313.931 149.59 312.275 149.477 310.598V308.466C150.116 307.962 150.358 307.251 150.187 306.334C150.834 305.254 151.424 304.075 151.964 302.781C152.319 306.093 152.44 309.404 152.319 312.73Z"
								fill="#15202A"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M145.923 333.34C146.101 339.998 145.866 346.636 145.213 353.237H144.502C144.786 346.486 145.262 339.849 145.923 333.34Z"
								fill="#43201E"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M213.433 356.08V362.475C228.136 362.226 242.825 362.106 257.492 362.12C261.479 360.08 265.508 359.483 269.573 360.343C270.383 366.469 270.497 372.623 269.928 378.82C269.751 380.724 269.04 382.38 267.796 383.794C266.958 382.92 266.126 382.92 265.309 383.794C265.465 384.839 265.707 385.905 266.02 386.992C265.664 388.413 264.954 389.593 263.888 390.545C264.499 391.355 265.21 392.3 266.02 393.387C265.529 396.798 265.053 400.238 264.598 403.692C265.387 407.209 265.508 410.762 264.954 414.351C264.684 413.953 264.328 413.719 263.888 413.64C263.419 403.457 262.829 393.274 262.111 383.083C261.145 382.458 260.079 382.223 258.913 382.373V368.871C246.627 369.525 234.425 370.946 222.316 373.135C220.987 372.602 219.808 372.367 218.763 372.424L217.697 373.49C218.841 374.236 219.31 375.302 219.118 376.688C218.642 376.688 218.408 376.453 218.408 375.977C215.899 374.613 213.057 374.137 209.88 374.556C210.84 373.739 210.719 373.262 209.525 373.135C206.05 374.137 202.497 374.606 198.865 374.556C185.051 373.817 171.307 372.396 157.649 370.292C156.285 370.349 154.977 370.583 153.741 371.003C153.975 367.421 153.741 363.868 153.03 360.343C150.557 359.931 148.191 359.22 145.924 358.211C144.737 356.776 144.261 355.113 144.502 353.237H145.213C145.241 354.004 145.476 354.715 145.924 355.369C167.47 356.584 189.03 357.529 210.591 358.211C211.408 345.896 211.408 333.574 210.591 321.259C209.589 322.19 208.402 322.78 207.038 323.035L207.748 323.746C190.779 324.286 173.723 323.099 156.583 320.193C155.56 319.923 154.849 319.333 154.451 318.416C156.732 318.637 158.864 318.402 160.847 317.706C161.323 317.706 161.558 317.471 161.558 316.995C162.034 317.23 162.503 317.471 162.979 317.706C163.483 318.345 164.194 318.587 165.111 318.416H170.796C171.982 319.361 173.162 319.361 174.349 318.416H175.77C175.77 318.892 176.005 319.127 176.481 319.127H183.587V319.838C186.429 320.783 189.272 320.783 192.114 319.838V318.416C192.754 318.921 192.996 319.631 192.825 320.548C194.19 320.747 195.369 320.513 196.378 319.838C196.854 320.072 197.323 320.314 197.799 320.548H201.353C201.58 317.798 201.338 315.19 200.642 312.731V307.757C200.408 303.465 200.642 299.201 201.353 294.966C201.836 295.747 202.305 296.458 202.774 297.097V299.229C202.994 301.972 203.47 304.58 204.195 307.046C204.771 310.329 205.012 313.648 204.906 316.995V320.548C205.822 320.719 206.533 320.477 207.038 319.838C207.684 300.992 208.395 282.046 209.17 262.987C209.738 264.018 210.093 265.204 210.235 266.54C210.47 260.145 210.712 253.749 210.946 247.354C211.181 249.251 211.422 251.141 211.657 253.039C211.778 279.737 212.133 306.385 212.723 332.984C212.303 337.973 212.062 343.182 212.012 348.618C212.467 351.176 212.936 353.663 213.433 356.08Z"
								fill="#121A23"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M288.758 277.199C288.659 276.112 288.182 275.402 287.337 275.068C288.097 268.402 287.621 261.765 285.915 255.17C284.729 250.196 283.549 245.221 282.362 240.247V233.851C281.168 233.368 280.699 232.416 280.941 231.009C280.195 228.713 279.726 226.347 279.52 223.902C279.392 221.607 278.923 219.475 278.099 217.507V216.796C277.679 212.561 276.734 208.539 275.256 204.715V202.584C273.579 193.218 271.682 183.979 269.571 174.869C268.882 173.37 268.647 171.714 268.86 169.895C269.393 168.594 270.104 167.408 270.992 166.342C270.765 162.625 269.699 159.072 267.794 155.682C265.883 154.36 264.106 152.939 262.465 151.418C262.777 143.801 259.466 138.471 252.516 135.429C249.56 134.925 246.717 134.335 243.988 133.653C244.649 132.892 244.891 131.947 244.699 130.81C244.273 131.805 243.562 132.516 242.567 132.942C242.631 135.209 242.162 137.341 241.146 139.338C240.201 139.814 239.249 140.283 238.303 140.759C236.548 141.569 235.838 142.99 236.172 145.023H193.534C192.901 144.589 192.191 144.355 191.402 144.312C190.279 143.147 189.803 141.725 189.981 140.048C189.064 137.774 188.595 135.408 188.56 132.942H187.138C187.33 131.805 187.089 130.86 186.428 130.1V134.363C183.251 134.1 180.174 134.456 177.189 135.429C176.536 136.879 176.294 138.421 176.479 140.048C175.583 142.131 174.162 143.786 172.215 145.023C171.632 146.878 171.277 148.768 171.149 150.708C170.382 151.589 169.785 152.534 169.373 153.55C167.497 155.547 165.72 157.679 164.043 159.946C163.844 159.292 163.489 158.823 162.977 158.525C162.273 161.701 162.039 165.02 162.266 168.473H160.845C161.35 169.113 162.06 169.355 162.977 169.184C162.032 176.056 161.08 182.921 160.134 189.792C159.587 193.807 159.111 197.837 158.713 201.873V202.584C158.116 204.602 157.64 206.734 157.292 208.979V213.243C155.792 222.048 154.847 231.051 154.449 240.247C153.092 246.486 152.147 252.882 151.607 259.434V261.566C150.441 264.202 149.972 267.045 150.186 270.093V271.514C149.582 275.665 149.105 279.928 148.764 284.306V289.28C148.167 289.522 147.933 289.998 148.054 290.701V297.097C147.414 297.602 147.173 298.312 147.343 299.229C146.824 303.72 146.348 308.218 145.922 312.731C145.922 314.06 146.163 315.246 146.633 316.284H148.054C148.104 314.202 148.573 312.312 149.475 310.599V308.467C150.115 307.963 150.356 307.252 150.186 306.335C150.832 305.255 151.422 304.075 151.962 302.782C152.318 306.094 152.438 309.405 152.318 312.731V318.416H154.449C156.731 318.636 158.862 318.402 160.845 317.705C161.321 317.705 161.556 317.471 161.556 316.995C162.032 317.229 162.501 317.471 162.977 317.705C163.481 318.345 164.192 318.586 165.109 318.416H170.794C171.981 319.361 173.16 319.361 174.347 318.416H175.768C175.768 318.892 176.003 319.127 176.479 319.127H183.585V319.837C186.428 320.782 189.27 320.782 192.113 319.837V318.416C192.752 318.92 192.994 319.631 192.823 320.548C194.188 320.747 195.367 320.512 196.376 319.837C196.853 320.072 197.322 320.313 197.798 320.548H201.351C201.578 317.798 201.337 315.19 200.64 312.731V307.756C200.406 303.464 200.64 299.201 201.351 294.965C201.834 295.747 202.303 296.457 202.772 297.097V299.229C202.992 301.972 203.468 304.58 204.193 307.046C204.769 310.329 205.011 313.648 204.904 316.995V320.548C205.821 320.718 206.531 320.477 207.036 319.837C207.683 300.991 208.393 282.046 209.168 262.987V241.668C210.326 229.012 210.802 216.221 210.589 203.294V196.188C211.285 193.971 211.52 191.597 211.3 189.082H221.959C222.954 191.988 223.423 195.065 223.38 198.32C223.259 199.023 223.494 199.499 224.091 199.741V206.847C224.269 211.758 224.979 216.491 226.223 221.06C227.253 229.332 228.205 237.625 229.065 245.932C229.286 251.198 229.762 256.414 230.487 261.566C231.311 268.182 232.021 274.812 232.618 281.463C233.819 290.673 234.772 299.911 235.461 309.178H237.593C238.168 307.664 238.524 306.008 238.659 304.203C238.858 304.857 239.213 305.326 239.725 305.625V307.756C240.819 308.325 242.006 308.559 243.278 308.467V304.914C243.754 305.859 244.223 306.811 244.699 307.756H246.831C247.407 306.484 247.762 305.063 247.897 303.493C248.039 304.829 248.394 306.015 248.963 307.046C249.673 307.991 250.384 307.991 251.095 307.046C251.884 307.003 252.594 306.769 253.227 306.335C254.236 307.01 255.422 307.245 256.78 307.046V302.071H257.49V306.335H260.333C260.809 306.101 261.278 305.859 261.754 305.625C264.17 306.328 266.537 306.094 268.86 304.914C271.042 305.006 273.174 304.772 275.256 304.203H278.099C277.622 295.157 276.67 286.153 275.256 277.199H273.124C273.629 276.56 274.339 276.318 275.256 276.489H276.677C277.75 285.045 279.051 293.572 280.586 302.071L282.007 303.493C281.709 294.411 280.643 285.407 278.809 276.489H280.23C281.879 285.187 283.066 293.956 283.784 302.782H286.626C285.674 293.778 284.729 284.775 283.784 275.778C284.643 275.259 285.589 275.018 286.626 275.068C287.351 284.37 288.531 293.608 290.179 302.782H293.022C291.664 294.247 290.243 285.72 288.758 277.199Z"
								fill="#1E3832"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M144.504 353.236C144.262 355.112 144.738 356.775 145.925 358.211C148.192 359.22 150.558 359.93 153.031 360.343C153.735 363.867 153.976 367.42 153.742 371.002C153.081 385.911 152.846 400.834 153.031 415.772C152.129 397.643 152.129 379.402 153.031 361.053C150.352 360.762 147.744 360.172 145.214 359.277C143.672 357.358 143.431 355.347 144.504 353.236Z"
								fill="#572621"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M209.878 374.555C209.175 374.434 208.699 374.668 208.457 375.265C207.888 376.36 207.654 377.547 207.746 378.819C206.247 381.661 205.302 384.745 204.904 388.057V388.767C203.895 390.203 203.419 391.859 203.483 393.742C202.004 397.629 201.059 401.651 200.64 405.822C198.856 410.811 197.67 416.02 197.087 421.456C195.985 428.314 194.799 435.186 193.534 442.064C192.489 445.205 192.013 448.524 192.112 452.013C190.705 458.558 189.76 465.188 189.27 471.911C187.742 478.363 186.79 485 186.427 491.808V492.519C185.44 495.909 184.964 499.462 185.006 503.178C183.841 508.707 183.13 514.392 182.874 520.233C182.626 526.309 181.915 532.229 180.742 537.999C172.364 540.451 163.837 540.806 155.16 539.065C153.49 538.234 151.834 537.402 150.185 536.578C149.517 533.572 149.283 530.488 149.475 527.34C150.193 522.017 150.669 516.567 150.896 510.995C151.436 506.518 151.671 502.013 151.607 497.493C152.779 491.972 153.255 486.287 153.028 480.438C153.412 468.947 153.881 457.457 154.449 445.973C154.186 435.896 153.71 425.827 153.028 415.771C152.843 400.834 153.078 385.911 153.739 371.002C154.975 370.582 156.283 370.348 157.647 370.291C171.305 372.394 185.049 373.816 198.863 374.555C202.495 374.605 206.048 374.136 209.523 373.134C210.717 373.261 210.838 373.738 209.878 374.555Z"
								fill="#16202A"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M263.886 413.641C264.597 425.48 265.428 437.326 266.373 449.172C267.404 450.956 268.235 452.853 268.86 454.857C270.296 453.294 271.12 451.403 271.348 449.172C271.618 449.57 271.973 449.804 272.414 449.883C273.472 456.392 273.949 463.022 273.835 469.78C273.757 476.794 274.226 483.666 275.256 490.388C275.761 501.112 276.827 511.771 278.454 522.367C278.93 527.817 278.93 533.261 278.454 538.711C278.184 539.109 277.829 539.344 277.388 539.422C277.48 538.796 277.246 538.327 276.677 538C272.293 538.519 267.674 538.995 262.82 539.422C258.663 539.265 254.52 539.024 250.384 538.711C250.64 536.998 249.929 535.932 248.252 535.513C252.772 534.817 252.537 534.462 247.542 534.447C247.556 528.741 247.201 523.056 246.476 517.392C246.206 516.994 245.85 516.76 245.41 516.682C245.168 513.306 244.692 509.987 243.989 506.733C243.846 501.346 243.136 496.137 241.857 491.099C241.047 484.185 240.101 477.313 239.014 470.491C238.623 466.319 237.913 462.29 236.882 458.41C236.705 454.438 235.994 450.643 234.75 447.04C234.097 443.992 233.62 440.915 233.329 437.802C232.732 433.14 231.78 428.642 230.487 424.3V423.589C230.551 421.706 230.074 420.05 229.065 418.615C229.03 416.071 228.554 413.705 227.644 411.509C227.132 407.529 226.187 403.734 224.802 400.139C224.993 399.002 224.752 398.057 224.091 397.296C223.97 395.619 223.729 393.963 223.38 392.322C223.018 389.444 222.421 386.601 221.604 383.794C221.334 383.396 220.978 383.162 220.538 383.084C219.955 380.98 219.479 378.848 219.117 376.688C219.308 375.302 218.839 374.236 217.695 373.49L218.761 372.424C219.806 372.367 220.986 372.602 222.314 373.135C234.423 370.946 246.625 369.525 258.912 368.871V382.373C260.077 382.224 261.143 382.458 262.11 383.084C262.827 393.274 263.417 403.457 263.886 413.641Z"
								fill="#15202B"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M150.188 536.578C151.836 537.402 153.492 538.234 155.162 539.065C163.839 540.806 172.366 540.451 180.744 537.999C179.138 541.055 178.428 544.374 178.613 547.948C177.952 547.188 177.71 546.243 177.902 545.106C170.753 546.157 163.647 545.923 156.583 544.395C154.657 544.26 153.002 543.549 151.609 542.263C151.808 540.906 151.573 539.719 150.898 538.71C150.855 537.921 150.621 537.211 150.188 536.578Z"
								fill="#141922"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M245.41 516.682C245.851 516.76 246.206 516.994 246.476 517.392C247.201 523.056 247.556 528.741 247.542 534.447C252.538 534.462 252.772 534.817 248.253 535.513C249.93 535.933 250.64 536.998 250.385 538.711C254.52 539.024 258.663 539.265 262.821 539.422C267.674 538.995 272.293 538.519 276.678 538C277.246 538.327 277.481 538.796 277.388 539.422V540.132C276.728 540.893 276.486 541.838 276.678 542.975C274.745 543.302 272.847 543.778 270.993 544.396C264.164 545.881 257.299 546.116 250.385 545.107C249.972 548.845 249.262 552.519 248.253 556.121C248.409 558.538 249.596 560.314 251.806 561.451C253.184 561.032 254.606 560.79 256.07 560.741C270.723 561.998 285.411 562.467 300.128 562.162C299.098 562.73 297.911 563.086 296.575 563.228C298.515 563.519 300.413 563.995 302.26 564.649C287.103 565.004 271.945 565.125 256.78 565.004C254.03 565.232 251.422 564.99 248.963 564.294C247.656 561.671 247.18 558.829 247.542 555.766V552.924C249.489 548.646 249.965 544.14 248.963 539.422C248.736 538.32 248.025 537.844 246.831 538C247.059 533.602 246.824 529.338 246.121 525.209C246.341 523.155 246.099 521.265 245.41 519.524V516.682Z"
								fill="#161822"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M146.634 542.264H151.608C153.001 543.55 154.657 544.261 156.582 544.396C162.545 547.785 165.629 552.994 165.821 560.029C153.967 560.491 142.121 561.202 130.289 562.161C128.726 562.161 127.305 561.92 126.025 561.451C127.034 560.442 127.745 559.255 128.157 557.897C128.392 557.421 128.633 556.952 128.868 556.476C133.117 554.103 137.147 551.495 140.949 548.659C143.024 546.812 144.921 544.68 146.634 542.264Z"
								fill="#1C232D"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M277.392 540.131C280.995 541.417 284.193 543.549 286.986 546.527C287.099 548.076 287.696 549.376 288.762 550.435C292.188 552.325 295.499 554.336 298.711 556.475C299.187 557.435 299.187 558.259 298.711 558.962C299.422 559.552 300.132 560.149 300.843 560.739C300.758 561.173 300.516 561.407 300.132 561.45C288.99 561.578 277.975 560.632 267.088 558.607C266.797 558.934 266.441 559.169 266.022 559.318C265.027 553.491 266.683 548.516 270.997 544.395C272.851 543.776 274.749 543.3 276.682 542.973C276.49 541.836 276.731 540.891 277.392 540.131Z"
								fill="#1C232D"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M146.634 542.264C144.922 544.68 143.024 546.812 140.949 548.659C141.852 545.625 143.742 543.493 146.634 542.264Z"
								fill="#1C232D"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M156.583 544.395C163.647 545.922 170.753 546.157 177.902 545.105C177.71 546.242 177.952 547.187 178.613 547.948C179.039 550.392 179.508 552.766 180.034 555.054V557.186C180.147 559.097 180.034 560.988 179.678 562.871C179.146 563.319 178.556 563.674 177.902 563.937C170.597 564.896 163.256 565.486 155.872 565.713H123.184C123.262 563.972 123.972 562.551 125.315 561.45H126.026C127.305 561.919 128.726 562.153 130.29 562.16C142.122 561.201 153.968 560.49 165.821 560.028C165.629 552.993 162.545 547.784 156.583 544.395Z"
								fill="#151C27"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M270.994 544.395C266.68 548.516 265.025 553.491 266.02 559.318C266.439 559.168 266.794 558.934 267.086 558.607C277.972 560.632 288.987 561.578 300.13 561.45V562.16C285.413 562.466 270.724 561.997 256.071 560.739C254.607 560.789 253.186 561.03 251.807 561.45C249.597 560.313 248.41 558.536 248.254 556.12C249.263 552.517 249.974 548.843 250.386 545.105C257.3 546.114 264.165 545.88 270.994 544.395Z"
								fill="#131D29"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M347.031 561.096C331.773 558.601 316.374 557.535 300.84 557.898C302.14 559.532 303.441 561.195 304.748 562.872C305.345 564.265 304.99 565.217 303.682 565.715C287.928 565.949 272.294 565.715 256.781 565.004C254.031 565.231 251.423 564.99 248.964 564.293C248.253 564.357 247.664 564.116 247.188 563.583C246.832 561.223 246.718 558.857 246.832 556.477C224.511 556.242 202.247 556.477 180.033 557.187C180.147 559.099 180.033 560.989 179.678 562.872C179.145 563.32 178.555 563.675 177.901 563.938C170.596 564.897 163.255 565.487 155.872 565.715C145.099 566.425 134.205 566.66 123.183 566.425C121.883 563.952 122.593 562.297 125.315 561.451C125.478 559.632 126.431 558.445 128.157 557.898C128.392 557.422 128.634 556.953 128.868 556.477C113.682 556.953 98.5171 558.019 83.3879 559.674C80.1474 559.951 77.0704 560.776 74.1497 562.162C73.3894 563.462 73.8655 564.286 75.571 564.649C80.922 565.999 86.3725 566.71 91.9154 566.781C171.506 570.071 251.096 570.071 330.686 566.781C336.435 566.539 342.12 565.828 347.741 564.649C351.451 563.092 351.209 561.906 347.031 561.096Z"
								fill="#B7B7B7"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M247.54 555.766C247.178 558.828 247.654 561.671 248.961 564.293C248.251 564.357 247.661 564.115 247.185 563.583C246.83 561.223 246.716 558.857 246.83 556.476C246.83 556 247.064 555.766 247.54 555.766Z"
								fill="#161822"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M128.157 557.896C127.745 559.254 127.034 560.441 126.025 561.45H125.314C125.478 559.63 126.43 558.444 128.157 557.896Z"
								fill="#1C232D"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M125.311 561.451C123.968 562.553 123.258 563.974 123.18 565.715H155.868C145.095 566.426 134.201 566.66 123.18 566.426C121.879 563.953 122.59 562.297 125.311 561.451Z"
								fill="#151C27"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M298.708 556.477C299.419 556.953 300.13 557.422 300.84 557.898C302.141 559.532 303.441 561.195 304.749 562.872C305.346 564.265 304.99 565.217 303.683 565.715C287.928 565.949 272.294 565.715 256.781 565.004C271.946 565.125 287.104 565.004 302.261 564.649C300.414 563.995 298.516 563.519 296.576 563.228C297.912 563.085 299.099 562.73 300.13 562.162V561.451C300.513 561.408 300.755 561.174 300.84 560.74C300.13 560.151 299.419 559.554 298.708 558.964C299.184 558.26 299.184 557.436 298.708 556.477Z"
								fill="#161822"
							/>
							<path
								d="M287.701 9.3849V79.1045C298.325 77.9818 306.092 76.7524 312.218 74.855V7.89258C304.046 8.43265 295.873 8.9372 287.701 9.3849Z"
								fill="#01754A"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M406.012 25.6381C405.834 24.7782 405.365 24.3021 404.591 24.2168C402.359 25.297 399.993 25.7731 397.484 25.6381C398.671 26.1142 399.851 26.5832 401.037 27.0593C400.306 32.7799 399.353 38.4649 398.195 44.1144C398.195 42.3094 397.953 40.5328 397.484 38.7847C397.356 37.804 396.88 37.2142 396.063 37.0081C392.418 42.8637 388.751 48.7832 385.048 54.7738C384.785 56.3372 384.43 57.8721 383.982 59.3929C385.361 60.4091 386.306 61.7166 386.825 63.3013C387.294 64.218 387.528 65.284 387.536 66.4991C387.557 66.8047 387.55 67.1032 387.536 67.3945L412.002 63.884C410.73 51.0146 408.741 38.2588 406.012 25.6309V25.6381Z"
								fill="#036141"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M436.216 188.371C435.456 184.143 434.646 179.929 433.807 175.715L361.266 186.118C361.266 186.396 361.252 186.673 361.252 186.95C361.657 194.141 362.24 201.319 362.965 208.482L437.353 197.815C436.991 194.667 436.614 191.519 436.223 188.371H436.216Z"
								fill="#036141"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M415.344 88.1797L376.558 93.7439C373.609 100.651 370.645 107.558 367.646 114.473H369.324L418.414 107.438C417.412 101.021 416.388 94.6037 415.351 88.1868L415.344 88.1797Z"
								fill="#036141"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M423.366 131.486L365.563 139.772C364.931 144.32 364.547 148.917 364.441 153.551C363.815 156.059 363.318 158.582 362.92 161.112L428.539 151.703C426.919 144.938 425.199 138.194 423.366 131.479V131.486Z"
								fill="#036141"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M439.923 222.211L366.082 232.799C366.245 233.858 366.402 234.924 366.572 235.983C369.045 247.502 371.056 259.107 372.613 270.804C372.847 269.859 373.089 268.906 373.323 267.961C372.016 258.574 370.595 249.215 369.06 239.891C369.294 239.302 369.536 238.705 369.77 238.115C370.765 239.87 371.355 241.767 371.547 243.8C372.897 252.32 374.197 260.848 375.455 269.382C376.024 272.118 376.5 272.005 376.876 269.027C375.192 258.737 373.529 248.433 371.902 238.115H374.745C376.052 249.037 377.594 259.931 379.364 270.804C379.655 271.131 380.01 271.365 380.43 271.514C380.664 270.683 380.906 269.859 381.14 269.027C379.456 258.744 377.793 248.44 376.166 238.115H379.008C379.975 249.172 381.751 260.066 384.338 270.804C383.677 260.087 382.377 249.428 380.43 238.825C381.019 238.193 381.73 237.958 382.561 238.115C384.125 249.066 386.022 259.959 388.247 270.804C388.481 270.093 388.723 269.382 388.957 268.672C387.245 258.545 386.065 248.362 385.404 238.115H387.536C388.552 246.401 389.618 254.694 390.734 262.987C391.451 265.63 392.283 268.238 393.221 270.804C393.789 270.477 394.024 270.008 393.932 269.382C395.118 271.315 396.895 272.268 399.261 272.225C401.031 271.834 402.694 271.834 404.236 272.225C405.785 272.083 406.851 271.372 407.433 270.093C407.007 260.734 405.707 251.496 403.525 242.379C402.949 239.87 403.07 237.504 403.88 235.272C405.33 236.487 406.041 238.143 406.012 240.247C406.773 249.79 408.073 259.27 409.921 268.672C410.574 270.086 411.406 270.328 412.408 269.382H414.54C414.703 269.809 415.058 270.043 415.606 270.093C416.529 269.233 417.595 268.764 418.803 268.672C419.28 269.617 419.749 269.617 420.225 268.672C421.17 267.727 422.122 267.727 423.067 268.672H424.489C425.121 268.238 425.832 268.004 426.62 267.961C427.125 268.601 427.836 268.842 428.752 268.672C430.11 268.871 431.296 268.636 432.305 267.961C432.938 267.528 433.648 267.293 434.437 267.251C434.942 267.89 435.652 268.132 436.569 267.961C437.202 267.528 437.912 267.293 438.701 267.251H443.675C443.896 264.735 443.661 262.361 442.965 260.144C442.148 247.481 441.131 234.839 439.923 222.211Z"
								fill="#036141"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M397.484 38.7833C397.953 40.5314 398.188 42.308 398.195 44.113C399.353 38.4635 400.305 32.7785 401.037 27.0579C399.851 26.5818 398.671 26.1128 397.484 25.6367C396.966 24.7768 396.724 23.8317 396.774 22.7942C393.043 23.7606 391.145 22.3394 391.089 18.5304C390.605 18.2746 390.129 18.033 389.667 17.8198C389.006 17.0594 388.765 16.1143 388.957 14.9773C391.636 13.9966 394.237 12.8099 396.774 11.4241C396.624 11.0049 396.39 10.6496 396.063 10.3582C394.18 10.0029 392.29 9.88919 390.378 10.0029C389.902 10.0029 389.667 9.76838 389.667 9.29226C389.575 8.02024 389.81 6.83349 390.378 5.73913C390.854 5.73913 391.089 5.50462 391.089 5.0285C389.817 4.45289 388.395 4.09758 386.825 3.96256C379.008 3.48644 371.191 3.48644 363.374 3.96256C361.775 4.10468 360.176 4.2326 358.57 4.36762V53.0598C361.491 52.5908 364.511 52.4416 367.638 52.6405C367.148 49.9473 367.858 47.7017 369.77 45.8896C379.321 40.8228 388.68 35.4931 397.84 29.9005C397.761 32.3948 397.164 34.7612 396.063 37.0067C396.88 37.2128 397.356 37.8026 397.484 38.7833Z"
								fill="#036141"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M163.41 9.69141V80.4557C172.577 81.7561 180.728 82.4526 188.382 82.8576V10.5655C180.053 10.3594 171.732 10.068 163.41 9.69852V9.69141Z"
								fill="#036141"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M264.605 81.1956C273.566 80.4495 281.17 79.7957 287.701 79.1064V9.38672C280.005 9.8131 272.309 10.1826 264.605 10.5308V81.1956Z"
								fill="#036141"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M312.219 7.89433V74.8568C314.329 74.203 316.241 73.471 318.01 72.6396C320 71.7016 326.296 68.6032 334.063 64.7587V6.35938C326.787 6.90656 319.503 7.41111 312.219 7.89433Z"
								fill="#036141"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M115.799 6.71484V69.2216C121.256 71.1332 129.024 73.5565 138.894 75.8731V8.37772C131.198 7.89449 123.495 7.3402 115.799 6.71484Z"
								fill="#036141"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M212.898 11.4238V83.5312C223.217 83.6236 232.27 83.3607 240.087 82.9627V11.4238H212.898Z"
								fill="#036141"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M3.08665 228.877C1.45221 241.121 0.507073 253.436 0.244141 265.83H2.37602C3.98204 266.341 5.64491 266.576 7.35042 266.54H10.1929C10.669 266.306 11.1381 266.064 11.6142 265.83C11.8416 266.931 12.5522 267.407 13.7461 267.251H18.0098C18.8697 267.77 19.8148 268.011 20.8523 267.961H24.4055C25.3506 268.907 26.3028 268.907 27.248 267.961H28.6692C29.1738 268.601 29.8844 268.843 30.8011 268.672C30.595 267.087 30.8295 265.666 31.5117 264.408C31.398 265.851 31.5117 267.272 31.8671 268.672C32.0305 267.791 32.3858 267.08 32.933 266.54V269.383H35.0649C34.8446 267.102 35.0791 264.97 35.7755 262.987C35.3847 265.481 35.8537 267.613 37.1968 269.383C38.1135 269.553 38.8241 269.312 39.3287 268.672C39.9611 269.106 40.6717 269.34 41.4605 269.383C42.1712 270.328 42.8818 270.328 43.5924 269.383C44.3812 269.425 45.0918 269.66 45.7243 270.093H47.8562C48.2968 270.171 48.6521 270.406 48.9221 270.804C49.2561 269.56 49.7322 268.381 50.3434 267.251C50.6987 258.013 50.8195 248.774 50.6987 239.536V235.983H52.8306C52.4753 247.829 52.3545 259.668 52.4753 271.515C53.392 267.613 53.861 263.584 53.8965 259.434C54.2518 251.624 54.3726 243.807 54.2518 235.983H57.0943C56.9735 245.825 56.5045 255.66 55.6731 265.474C55.9076 267.485 56.1492 269.503 56.3837 271.515C58.075 260.23 58.7856 248.86 58.5156 237.404H61.3581C60.7896 248.767 60.3135 260.137 59.9368 271.515H62.0687C62.0687 260.137 62.3032 248.767 62.7794 237.404H65.6219C71.3851 237.66 77.3117 237.895 83.3876 238.115C83.5084 234.967 83.6007 231.812 83.6931 228.664L3.35669 221.664C3.2572 224.066 3.15771 226.475 3.08665 228.877Z"
								fill="#036141"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M18.0957 105.824L72.1745 110.535C71.8334 107.337 71.4425 104.139 70.9522 100.963C69.737 97.5306 68.5858 94.0628 67.4843 90.5665L22.4305 86.6367C20.8956 93.011 19.453 99.4067 18.0957 105.824Z"
								fill="#036141"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M58.1604 0.765398C55.9362 0.644592 53.9251 1.23441 52.1201 2.54197C53.1434 2.69831 54.0885 3.05362 54.9626 3.60791C54.6357 4.17641 54.1667 4.41092 53.5413 4.31854C54.0459 4.9581 54.7565 5.19971 55.6732 5.02916V7.87167H49.9882C49.91 9.30003 50.3862 10.4868 51.4095 11.4248H52.1201C52.4328 12.5405 53.1434 13.3719 54.252 13.912C52.916 14.0541 51.7292 14.4094 50.6988 14.9779C53.5556 14.5089 54.5007 15.4541 53.5413 17.8205V18.5311C52.8378 18.4103 52.3617 18.6448 52.1201 19.2417V21.3736H42.1713C41.9936 23.8892 41.5175 26.2556 40.75 28.4799C39.009 31.763 37.5878 35.5506 36.4863 39.8499C36.5929 39.1037 36.4721 38.3931 36.131 37.718C33.4874 45.9613 31.0073 54.2543 28.6836 62.5758L60.6476 65.3614V61.8794C60.207 61.8012 59.8517 61.5667 59.5817 61.1687C57.8477 53.4655 55.3605 46.1247 52.1201 39.1393C51.978 36.9932 51.7363 34.8613 51.4095 32.7436C51.2531 31.9122 51.4876 31.2016 52.1201 30.6117C53.2358 31.6564 54.188 32.8431 54.9626 34.1649C59.6954 38.187 64.9043 41.5057 70.5964 44.1137C71.2857 45.6131 71.5202 47.2689 71.307 49.0881C73.1262 48.8749 74.782 49.1094 76.2814 49.7987C77.4682 50.7438 78.6478 51.6961 79.8346 52.6412C79.1737 51.4047 79.1737 50.218 79.8346 49.0881C80.9929 50.4454 82.407 51.9306 84.0983 53.43C85.5693 54.7304 87.0048 55.8177 88.3266 56.7131C89.2362 57.2247 90.2239 57.7648 91.2828 58.3262V4.49619C80.2396 3.39472 69.1965 2.15112 58.1604 0.765398Z"
								fill="#036141"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M10.9017 145.733C10.4824 147.311 10.1271 148.917 9.81445 150.544L82.2629 156.854C81.3533 154.644 80.7777 152.363 80.5432 149.997H79.8326C79.3351 146.941 79.1006 143.864 79.1219 140.759C79.2143 139.074 79.1575 137.404 78.9585 135.741L13.4529 130.035C12.5433 135.258 11.6905 140.488 10.9017 145.74V145.733Z"
								fill="#036141"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M5.92993 185.528C5.53909 189.408 5.19088 193.295 4.87109 197.182L84.099 204.09C84.1132 200.508 84.1132 196.927 84.099 193.345C84.0208 189.422 83.8573 185.5 83.6228 181.584L6.97456 174.904C6.16444 178.315 5.81623 181.854 5.92993 185.528Z"
								fill="#036141"
							/>
							<path
								d="M91.2812 4.49538V58.3254C95.5095 60.5852 100.889 63.2643 107.256 65.9433C108.585 66.5047 111.47 67.6986 115.798 69.2122V6.70544C107.626 6.04455 99.4535 5.3055 91.2812 4.48828V4.49538Z"
								fill="#01754A"
							/>
							<path
								d="M138.895 8.37695V75.8723C144.445 77.1728 150.648 78.4377 157.485 79.5605C159.51 79.8945 161.478 80.1858 163.411 80.463V9.69872C155.239 9.3363 147.067 8.89571 138.895 8.37695Z"
								fill="#01754A"
							/>
							<path
								d="M210.59 11.4236C205.381 11.1891 200.165 10.9475 194.956 10.713C192.767 10.6703 190.572 10.6135 188.383 10.5566V82.8488C195.297 83.2112 201.814 83.3391 208.337 83.4599C209.886 83.4883 211.4 83.5096 212.899 83.5239V11.4165H210.59V11.4236Z"
								fill="#01754A"
							/>
							<path
								d="M242.57 11.4247H240.09V82.9636C247.921 82.5656 254.508 82.0326 259.817 81.592C261.458 81.457 263.05 81.322 264.606 81.1941V10.5293C257.266 10.8562 249.918 11.1689 242.57 11.4247Z"
								fill="#01754A"
							/>
							<path
								d="M334.06 6.35889V64.7583C340.74 61.4467 348.514 57.588 355.556 54.0633C355.762 53.9567 356.771 53.4593 356.978 53.3527C357.503 53.2461 358.036 53.1537 358.569 53.0613V4.36914C350.397 5.07266 342.225 5.74064 334.053 6.35889H334.06Z"
								fill="#01754A"
							/>
							<path
								d="M437.344 197.816L362.955 208.483C363.787 216.612 364.824 224.721 366.075 232.801L439.916 222.212C439.134 214.076 438.282 205.939 437.336 197.816H437.344Z"
								fill="#01754A"
							/>
							<path
								d="M428.538 151.703L362.919 161.112C362.023 166.811 361.696 172.581 361.952 178.423C361.526 180.881 361.298 183.447 361.256 186.119L433.797 175.715C432.191 167.678 430.435 159.669 428.531 151.703H428.538Z"
								fill="#01754A"
							/>
							<path
								d="M420.579 121.571C419.869 116.853 419.137 112.141 418.405 107.43L369.315 114.465H369.769C369.656 119.382 368.945 124.122 367.638 128.677C366.763 132.344 366.081 136.047 365.562 139.77L423.365 131.484C422.462 128.173 421.539 124.868 420.579 121.571Z"
								fill="#01754A"
							/>
							<path
								d="M412.762 72.539C412.549 69.6467 412.293 66.7687 412.002 63.8906L387.535 67.4011C387.464 68.8224 386.995 70.066 386.114 71.1248C382.959 78.6646 379.768 86.2043 376.549 93.7441L415.335 88.1799C414.489 82.9639 413.629 77.755 412.755 72.539H412.762Z"
								fill="#01754A"
							/>
							<path
								d="M3.35547 221.665L83.6919 228.664C83.9264 220.478 84.0685 212.284 84.1041 204.091L4.87621 197.184C4.21533 205.327 3.71078 213.492 3.36257 221.658L3.35547 221.665Z"
								fill="#01754A"
							/>
							<path
								d="M8.77054 157.813C8.09545 163.007 7.62643 168.216 7.34929 173.446C7.21427 173.93 7.08636 174.413 6.97266 174.903L83.6209 181.583C83.2585 175.543 82.7042 169.51 81.9652 163.498C82.086 161.842 82.1997 160.179 82.3205 158.523C82.5905 158.921 82.9458 159.156 83.3864 159.234C82.9672 158.452 82.5976 157.656 82.2636 156.853L9.81517 150.543C9.36036 152.916 8.99794 155.333 8.77054 157.813Z"
								fill="#01754A"
							/>
							<path
								d="M13.4524 130.027L78.958 135.734C78.9012 135.272 78.8444 134.817 78.7662 134.355C78.5672 133.701 78.2119 133.232 77.7002 132.934C76.4282 127.846 75.4831 122.63 74.8577 117.3C74.9785 116.597 74.744 116.121 74.1471 115.879C73.2304 114.927 72.6335 113.74 72.3705 112.326C72.3137 111.729 72.2355 111.132 72.1716 110.528L18.0928 105.816C16.4015 113.846 14.8524 121.919 13.4453 130.02L13.4524 130.027Z"
								fill="#01754A"
							/>
							<path
								d="M22.4297 86.638L67.4835 90.5678C66.716 88.1303 65.9698 85.6716 65.2663 83.1986C64.911 81.3154 64.7973 79.4252 64.911 77.5136C64.4917 74.6 63.7811 71.7575 62.7791 68.986C61.8127 68.2115 61.102 67.2663 60.6472 66.1435V65.3618L28.6832 62.5762C26.4518 70.5636 24.3697 78.5795 22.4297 86.638Z"
								fill="#01754A"
							/>
							<path
								d="M173.773 69.1364C170.561 69.1364 167.697 68.5455 165.182 67.3636C162.682 66.1818 160.697 64.553 159.227 62.4773C157.758 60.4015 156.992 58.0227 156.932 55.3409H166.477C166.583 57.1439 167.341 58.6061 168.75 59.7273C170.159 60.8485 171.833 61.4091 173.773 61.4091C175.318 61.4091 176.682 61.0682 177.864 60.3864C179.061 59.6894 179.992 58.7273 180.659 57.5C181.341 56.2576 181.682 54.8333 181.682 53.2273C181.682 51.5909 181.333 50.1515 180.636 48.9091C179.955 47.6667 179.008 46.697 177.795 46C176.583 45.303 175.197 44.947 173.636 44.9318C172.273 44.9318 170.947 45.2121 169.659 45.7727C168.386 46.3333 167.394 47.0985 166.682 48.0682L157.932 46.5L160.136 21.9545H188.591V30H168.25L167.045 41.6591H167.318C168.136 40.5076 169.371 39.553 171.023 38.7955C172.674 38.0379 174.523 37.6591 176.568 37.6591C179.371 37.6591 181.871 38.3182 184.068 39.6364C186.265 40.9545 188 42.7652 189.273 45.0682C190.545 47.3561 191.174 49.9924 191.159 52.9773C191.174 56.1136 190.447 58.9015 188.977 61.3409C187.523 63.7652 185.485 65.6742 182.864 67.0682C180.258 68.447 177.227 69.1364 173.773 69.1364ZM216.273 69.5227C212.364 69.5076 209 68.5455 206.182 66.6364C203.379 64.7273 201.22 61.9621 199.705 58.3409C198.205 54.7197 197.462 50.3636 197.477 45.2727C197.477 40.197 198.227 35.8712 199.727 32.2955C201.242 28.7197 203.402 26 206.205 24.1364C209.023 22.2576 212.379 21.3182 216.273 21.3182C220.167 21.3182 223.515 22.2576 226.318 24.1364C229.136 26.0152 231.303 28.7424 232.818 32.3182C234.333 35.8788 235.083 40.197 235.068 45.2727C235.068 50.3788 234.311 54.7424 232.795 58.3636C231.295 61.9848 229.144 64.75 226.341 66.6591C223.538 68.5682 220.182 69.5227 216.273 69.5227ZM216.273 61.3636C218.939 61.3636 221.068 60.0227 222.659 57.3409C224.25 54.6591 225.038 50.6364 225.023 45.2727C225.023 41.7424 224.659 38.803 223.932 36.4545C223.22 34.1061 222.205 32.3409 220.886 31.1591C219.583 29.9773 218.045 29.3864 216.273 29.3864C213.621 29.3864 211.5 30.7121 209.909 33.3636C208.318 36.0152 207.515 39.9848 207.5 45.2727C207.5 48.8485 207.856 51.8333 208.568 54.2273C209.295 56.6061 210.318 58.3939 211.636 59.5909C212.955 60.7727 214.5 61.3636 216.273 61.3636ZM260.335 69.5227C256.426 69.5076 253.063 68.5455 250.244 66.6364C247.441 64.7273 245.282 61.9621 243.767 58.3409C242.267 54.7197 241.525 50.3636 241.54 45.2727C241.54 40.197 242.29 35.8712 243.79 32.2955C245.305 28.7197 247.464 26 250.267 24.1364C253.085 22.2576 256.441 21.3182 260.335 21.3182C264.229 21.3182 267.578 22.2576 270.381 24.1364C273.199 26.0152 275.366 28.7424 276.881 32.3182C278.396 35.8788 279.146 40.197 279.131 45.2727C279.131 50.3788 278.373 54.7424 276.858 58.3636C275.358 61.9848 273.206 64.75 270.403 66.6591C267.6 68.5682 264.244 69.5227 260.335 69.5227ZM260.335 61.3636C263.002 61.3636 265.131 60.0227 266.722 57.3409C268.313 54.6591 269.1 50.6364 269.085 45.2727C269.085 41.7424 268.722 38.803 267.994 36.4545C267.282 34.1061 266.267 32.3409 264.949 31.1591C263.646 29.9773 262.108 29.3864 260.335 29.3864C257.684 29.3864 255.563 30.7121 253.972 33.3636C252.381 36.0152 251.578 39.9848 251.562 45.2727C251.562 48.8485 251.919 51.8333 252.631 54.2273C253.358 56.6061 254.381 58.3939 255.699 59.5909C257.017 60.7727 258.562 61.3636 260.335 61.3636Z"
								fill="white"
							/>
						</svg>
					</div>

					<div className="flex flex-col items-center justify-center gap-1">
						<h1 className="text-center font-bold text-foreground text-xl md:text-2xl lg:text-3xl">
							Ops, algo deu errado
						</h1>
						<div className="max-w-[600px] text-balance text-center font-normal text-muted-foreground text-sm md:text-lg lg:text-xl">
							Nossa equipe já recebeu notificação desse erro e vai trabalhar
							para que isso nunca mais aconteça. Desculpe o inconveniente.
							<strong className="text-foreground">
								{" "}
								As vezes atualizar a página resolve.
							</strong>
						</div>
					</div>
				</div>
			</main>
		);
	},
});

function RootDocument({ children }: { children: React.ReactNode }) {
	const loaderData = useLoaderData({ from: Route.id });
	const { defaultTheme } = loaderData || { defaultTheme: "light" };

	return (
		<RelayEnvironmentProvider environment={environment}>
			<ThemeProvider defaultTheme={defaultTheme}>
				<html lang="pt-BR" className="light">
					<head>
						<HeadContent />
					</head>
					<body className="bg-background">
						<UploadProgressProvider>
							<TopLoader />
							<Toaster richColors position="top-right" />
							{children}

							{import.meta.env.DEV && (
								<TanStackDevtools
									config={{
										position: "bottom-right",
									}}
									plugins={[
										{
											name: "Tanstack Router",
											render: <TanStackRouterDevtoolsPanel />,
										},
									]}
								/>
							)}
							<Scripts />
						</UploadProgressProvider>
					</body>
				</html>
			</ThemeProvider>
		</RelayEnvironmentProvider>
	);
}
